import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DraggableHeaderCell from "./DraggableHeaderCell";
import { applyColumnOrder, reorderArray } from "./column-order-utils";

function useDraggableColumnOrder({
  baseColumns,
  columnOrder,
  onColumnOrderChange,
  lockedKeys = [],
  draggableKeys = null,
}) {
  const [dragOverKey, setDragOverKey] = useState(null);
  const draggingKeyRef = useRef(null);
  const orderedColumnsKeysRef = useRef([]);

  const lockedKeySet = useMemo(
    () => new Set(Array.isArray(lockedKeys) ? lockedKeys : []),
    [lockedKeys],
  );

  const draggableKeySet = useMemo(() => {
    if (!Array.isArray(draggableKeys)) return null;
    return new Set(draggableKeys);
  }, [draggableKeys]);

  const isColumnDraggable = useCallback(
    (columnKey) => {
      if (!columnKey || lockedKeySet.has(columnKey)) return false;
      if (draggableKeySet && !draggableKeySet.has(columnKey)) return false;
      return true;
    },
    [draggableKeySet, lockedKeySet],
  );

  const handleHeaderDragState = useCallback((event) => {
    if (!event) return;
    const { type, columnKey } = event;

    if (type === "start") {
      draggingKeyRef.current = columnKey;
      setDragOverKey(null);
      return;
    }

    if (type === "over") {
      const dragging = draggingKeyRef.current;
      if (dragging && dragging !== columnKey) {
        setDragOverKey((prev) => (prev === columnKey ? prev : columnKey));
      }
      return;
    }

    if (type === "leave") {
      setDragOverKey((prev) => (prev === columnKey ? null : prev));
      return;
    }

    if (type === "end") {
      draggingKeyRef.current = null;
      setDragOverKey(null);
    }
  }, []);

  const handleReorderColumn = useCallback(
    (fromKey, toKey) => {
      if (!fromKey || !toKey || fromKey === toKey) return;
      if (!isColumnDraggable(fromKey) || !isColumnDraggable(toKey)) return;

      const currentKeys = orderedColumnsKeysRef.current;
      if (!Array.isArray(currentKeys) || !currentKeys.length) return;

      const fromIdx = currentKeys.indexOf(fromKey);
      const toIdx = currentKeys.indexOf(toKey);
      if (fromIdx === -1 || toIdx === -1) return;

      const next = reorderArray(currentKeys, fromIdx, toIdx);
      onColumnOrderChange?.(next);
    },
    [isColumnDraggable, onColumnOrderChange],
  );

  const orderedColumns = useMemo(() => {
    const ordered = applyColumnOrder(baseColumns, columnOrder);
    return ordered.map((column) => ({
      ...column,
      onHeaderCell: () => ({
        columnKey: column.key,
        draggable: isColumnDraggable(column.key),
        isDragging: draggingKeyRef.current === column.key,
        isDragOver: dragOverKey === column.key,
        onReorder: handleReorderColumn,
        onDragStateChange: handleHeaderDragState,
      }),
    }));
  }, [
    baseColumns,
    columnOrder,
    dragOverKey,
    handleHeaderDragState,
    handleReorderColumn,
    isColumnDraggable,
  ]);

  useEffect(() => {
    orderedColumnsKeysRef.current = orderedColumns
      .map((column) => column.key)
      .filter(Boolean);
  }, [orderedColumns]);

  const tableComponents = useMemo(
    () => ({
      header: {
        cell: DraggableHeaderCell,
      },
    }),
    [],
  );

  return {
    orderedColumns,
    tableComponents,
  };
}

export default useDraggableColumnOrder;
